import PropTypes from 'prop-types';
import React, {Component} from 'react';
import TableFloatToolbar from './TableFloatToolbar';
import Table from './Table';
import Sort from '../../../../shared/constants/CRUD'

type TableProviderState = {
    page: number,
    sizePerPage: number,
    searchText: string,
    sort: Sort,
    filters: {},
    selected: [],
    hoverRow: {},
    loading: boolean
};

type TableProviderProps = {
    state?: TableProviderState,
    height?: string,

    refresh?: Boolean,
    search?: Boolean,
    pageable?: Boolean,
    loading?: Boolean,
    offlineSortable?: Boolean,
    offlinePageable?: Boolean,
    data: [],
    columns: [],
    keyField: string,
    totalSize: number,
    singleSelection?: Boolean,
    selected?: [],

    onEditData?: PropTypes.func,
    onViewData?: PropTypes.func,

    onDeleteData?: PropTypes.func,
    onDeleteMessage?: PropTypes.func | string,

    onHoverRenderer?: PropTypes.func,
    selectedRowActions?: [],
    onRowSelected?: PropTypes.func,

    onLoadData: PropTypes.func,
    onSearch?: PropTypes.func,
    onDownload?: PropTypes.func,
    onExport?: PropTypes.func,
    onShare?: PropTypes.func,
    hideShare?: Boolean,
};

const DefaultKeyField = 'id';

const TableChangeType = {
    Search: 'search',
    Sort: 'sort',
    Filter: 'filter',
    Pagination: 'pagination'
};

const INIT_TABLE_PROVIDER_STATE = {
    page: 1,
    sizePerPage: 10,
    searchText: null,
    sort: null,
    filters: null,
    selected: [],
    hoverRow: null,
    isTableScroll: false,
    loading: true
};

class TableProvider extends Component<TableProviderProps, TableProviderState> {
    constructor(props) {
        super(props);
        this.state = props.state ? {...INIT_TABLE_PROVIDER_STATE, ...props.state,} : INIT_TABLE_PROVIDER_STATE;
    }

    static defaultProps = {
        ...this.props,
        keyField: DefaultKeyField
    };

    handleTableChange = (type, {page, sizePerPage, searchText, sortOrder, sortField, filters, ...rest}) => {
        let selected = [...this.state.selected]
        if ([TableChangeType.Search, TableChangeType.Filter].includes(type)) {
            page = INIT_TABLE_PROVIDER_STATE.page
            selected = INIT_TABLE_PROVIDER_STATE.selected
        }
        let sort: Sort = sortOrder && sortField ? {order: sortOrder, field: sortField} : undefined
        this.setState(() => ({
            // page,
            // sizePerPage,
            // searchText,
            // sort,
            // filters,
            selected
        }));

        if (this.props.onLoadData) {
            this.props.onLoadData(page, sizePerPage, searchText, sort, filters);
        }
    };

    handleSearch = (searchText) => {
        this.setState(() => ({
            selected: INIT_TABLE_PROVIDER_STATE.selected
        }));
        const tableState = this.props.state || this.state;
        let {sizePerPage, sortOrder, sortField, filters} = tableState;
        let sort: Sort = sortOrder && sortField ? {order: sortOrder, field: sortField} : undefined
        if (this.props.onLoadData) {
            this.props.onLoadData(
                INIT_TABLE_PROVIDER_STATE.page,
                sizePerPage,
                searchText,
                sort,
                filters,
                undefined
            );
        }
    }

    handleUnselect = () => {
        this.setState(() => ({
            selected: [],
        }));
    };

    handleOnSelect = (row, isSelect) => {
        if (!this.props.disableSelect) {
            let nextState = [];
            const selected = this.props.singleSelection ? [] : this.state.selected
            if (isSelect) {
                nextState = [...selected, row[this.props.keyField]];
            } else {
                nextState = selected.filter(x => x !== row[this.props.keyField])
            }

            if (this.props.onRowSelected) {
                this.props.onRowSelected(nextState[nextState.length - 1], nextState);
            }
            this.setState({selected: nextState});
        }
    };

    handleOnSelectAll = (isSelect, rows) => {
        const ids = rows.map(r => r[this.props.keyField]);
        if (isSelect) {
            this.setState({
                selected: ids
            });
        } else {
            this.setState({
                selected: []
            });
        }
        if (this.props.onRowSelected) {
            this.props.onRowSelected(this.state.selected, isSelect ? ids : []);
        }
    };

    handleOnDelete = (selectedItems) => {
        if (this.props.onDeleteData) {
            this.props.onDeleteData(selectedItems);
            this.setState({
                selected: []
            });
        }
    };

    handleOnEdit = (selectedItem) => {
        if (this.props.onEditData) {
            this.props.onEditData(selectedItem);
            this.setState({
                selected: []
            });
        }
    };

    handleOnDownload = (selectedItems) => {
        if (this.props.onDownload) {
            this.props.onDownload(selectedItems);
            this.setState({
                selected: []
            });
        }
    };

    handleOnExport = (selectedItems) => {
        if (this.props.onExport) {
            this.props.onExport(selectedItems);
            this.setState({
                selected: []
            });
        }
    };

    handleOnShare = (selectedItems) => {
        if (this.props.onShare) {
            this.props.onShare(selectedItems);
            this.setState({
                selected: []
            });
        }
    };

    handleOnView = (selectedItem) => {
        if (this.props.onViewData) {
            this.props.onViewData(selectedItem);
        }
    };

    handleRowHover = (row) => {
        this.setState({
            hoverRow: row,
        });
    };

    handleRowBlur = (row) => {
        this.setState({
            hoverRow: null,
        });
    };

    render() {
        const {page, sizePerPage, searchText, sort, filters} = this.props.state || this.state;
        const selected = this.state.selected;
        const loading = this.props.loading;
        const {onLoadData, data, ...rest} = this.props;
        const {selectedRowActions} = this.props;
        const showRowHover = this.props.onHoverRenderer && this.state.hoverRow !== null;
        const clickToSelect = !this.props.onHoverRenderer || this.props.clickToSelect;
        const refresh = this.props.refresh === undefined ? true : this.props.refresh
        const search = this.props.search === undefined ? true : this.props.search
        const pageable = this.props.pageable === undefined ? true : this.props.pageable
        const offlineSortable = !!this.props.offlineSortable
        const offlinePageable = !!this.props.offlinePageable

        const startIndex = (this.state.page - 1) * this.state.sizePerPage;
        const endIndex = pageable ? Math.min(startIndex + sizePerPage, this.props.data.length) : this.props.data.length;
        let d = this.props.onLoadData
            ? this.props.data
            : this.props.data
                ? this.props.data.slice(startIndex, endIndex)
                : [];

        return (
            <React.Fragment>
                <Table {...rest}
                       data={d}
                       height={this.props.height}
                       refresh={refresh}
                       search={search}
                       pageable={pageable}
                       offlineSortable={offlineSortable}
                       offlinePageable={offlinePageable}
                       loading={loading}
                       page={page}
                       sizePerPage={sizePerPage}
                       searchText={searchText}
                       sort={sort}
                       filters={filters}
                       isTableScroll={this.props.isTableScroll}
                       selected={selected}
                       clickToSelect={clickToSelect}
                       onSearch={this.handleSearch}
                       onTableChange={this.handleTableChange}
                       onRowSelected={this.handleOnSelect}
                       onSelectAll={this.handleOnSelectAll}
                       onRowBlur={this.handleRowBlur}
                       onRowHover={this.handleRowHover}
                       singleSelection={this.props.singleSelection}
                       onDownload={this.props.onDownload ? this.handleOnDownload : null}
                       onExport={this.props.onExport ? this.handleOnExport : null}
                       onShare={this.props.onShare ? this.handleOnShare : null}
                       hideShare={this.props.hideShare}
                />
                {this.props.singleSelection
                    ? null
                    : <TableFloatToolbar
                        selectedItems={this.state.selected}
                        data={data}
                        onEdit={this.props.onEditData ? this.handleOnEdit : null}
                        onView={this.props.onViewData ? this.handleOnView : null}
                        onDeleteMessage={this.props.onDeleteMessage}
                        onDelete={this.props.disableDelete ? null : this.handleOnDelete}
                        onUnselectAll={this.handleUnselect}
                        additionalActions={selectedRowActions}
                        hasModifyPermission={this.props.hasModifyPermission}
                    />
                }

                {showRowHover && this.props.onHoverRenderer(this.state.hoverRow)}
            </React.Fragment>
        );
    }
}

export default TableProvider;
